// First-strong-character direction — the Unicode `dir="auto"` rule (matches the
// mobile twin and the browser): a string reads in the direction of its first
// STRONG directional character (RTL for Arabic/Hebrew, LTR for Latin), so a
// reply — or a single block within it — is laid out by its OWN language, not
// the app's UI direction. Neutral characters (digits, punctuation, whitespace,
// markdown markers) are skipped; a string with no strong character at all
// falls back to the app's (live) UI direction, read straight off
// `document.documentElement.dir` — the same attribute `ThemeProviderInner`
// keeps in sync with the active language (see `ui/src/theme/theme-provider.tsx`).
//
// Ranges are expressed as numeric code points (not literal glyphs or regex
// escapes) to keep this source pure-ASCII — the RTL presentation-forms block
// ends at U+FEFF, which as a literal is an invisible BOM and corrupts the file.
const RTL_RANGES: readonly [number, number][] = [
  [0x0590, 0x05ff], // Hebrew
  [0x0600, 0x06ff], // Arabic
  [0x0750, 0x077f], // Arabic Supplement
  [0x08a0, 0x08ff], // Arabic Extended-A
  [0xfb1d, 0xfdff], // Hebrew + Arabic presentation forms-A
  [0xfe70, 0xfeff], // Arabic presentation forms-B
];
const LTR_RANGES: readonly [number, number][] = [
  [0x41, 0x5a], // A–Z
  [0x61, 0x7a], // a–z
  [0xc0, 0x24f], // Latin-1 Supplement + Latin Extended-A/B
];

function inRanges(code: number, ranges: readonly [number, number][]): boolean {
  for (const [lo, hi] of ranges) {
    if (code >= lo && code <= hi) return true;
  }
  return false;
}

function getAppDirection(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
}

export function isRtlText(text: string): boolean {
  for (const ch of text) {
    const code = ch.codePointAt(0);
    if (code === undefined) continue;
    if (inRanges(code, RTL_RANGES)) return true;
    if (inRanges(code, LTR_RANGES)) return false;
  }
  return getAppDirection();
}
